import React, { useEffect, useState, useRef } from 'react';
import i18n from './i18n';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Gamepad2,
  Store,
  Trophy,
  Users,
  TrendingUp,
  Smartphone,
  Settings,
  Zap,
  ChevronRight,
  Building,
  Briefcase,
  ClipboardCheck,
  Info,
  CheckCircle,
  PlayCircle,
  Menu,
  X,
  Leaf
} from 'lucide-react';

import ContactFormModal from './components/ContactFormModal';
import SplitText from "./components/SplitText";
import Aurora from './components/Aurora';
import LanguageSelector from './components/LanguageSelector';
import FAQSection from './components/FAQSection';
import proffaVideo from './assets/proffa.mp4';
import proffaFiVideo from './assets/proffa-fi.mp4';
import proffaPoster from './assets/proffa-cover.png';
import gameplayVideo from './assets/gameplay.mp4';
import gameplayPoster from './assets/gameplay-cover.jpg';
import locationVideo from './assets/location.mp4';
import locationPoster from './assets/location-cover.jpg';
import winnersVideo from './assets/app-winners.mp4';
import winnersPoster from './assets/app-winners-cover.jpg';

const LINKS = {
  sciencedirect: "https://www.sciencedirect.com/science/article/pii/S0148296321002666#ab010",
  mastercard: "https://www.mastercardservices.com/en/advisors/consumer-engagement-loyalty-consulting/insights/impact-gamification-loyalty-strategies",
  game: "https://codespace.fi/fusionx/51-demo.html",
  qr: "/qr.jpg",
  instructions: "https://codespace.fi/fusionx/saas/users/51-demo/instructions.php?id=113&user=51",
  score: "https://codespace.fi/fusionx/saas/users/51-demo/bestplayers_fx-weekly.php",
  codespace: "https://codespace.fi",
  menevafi: "https://meneva.fi/fi",
  menevaen: "https://meneva.fi/en",
  familyCreatives: "https://www.familycreatives.fi/",
  jamk: "https://www.jamk.fi/",
  laatukeskus: "https://laatukeskus.fi/",
  stripeClimate: "https://stripe.com/climate",
  privacy: "https://codespace.fi/fusionx/saas/public/privacy-policy.php",
  terms: "https://codespace.fi/fusionx/saas/public/terms.php",
};


const LinkInline = ({ href, children, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`text-purple-400 underline hover:text-purple-300 ${className}`}
  >
    {children}
  </a>
);

const borderColors = {
  kimmo: 'border-purple-400',
  heidi: 'border-pink-400',
  arto:  'border-indigo-400',
};
const roleColors = {
  kimmo: 'text-purple-400',
  heidi: 'text-pink-400',
  arto:  'text-indigo-400',
};

const TEAM_LINK_COMPONENTS = {
  kimmo: {
    codespace: (<LinkInline href={LINKS.codespace} />),
  },
  arto: {
    menevafi: (<LinkInline href={LINKS.menevafi} />),
    menevaen: (<LinkInline href={LINKS.menevaen} />),
  },
  heidi: {
    fc:   (<LinkInline href={LINKS.familyCreatives} />),
    jamk: (<LinkInline href={LINKS.jamk} />),
  }
};

const getProductStepMedia = (isFinnish) => ([
  { video: isFinnish ? proffaFiVideo : proffaVideo, poster: proffaPoster },
  { video: gameplayVideo, poster: gameplayPoster },
  { video: locationVideo, poster: locationPoster },
  { video: winnersVideo, poster: winnersPoster },
]);

const cn = (...classes) => classes.filter(Boolean).join(' ');
const Button = React.forwardRef(({ className, variant, size, children, ...props }, ref) => {
  const base = "font-semibold rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none inline-flex items-center justify-center";
  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
  };
  const sizes = { lg: "h-12 px-8 text-lg", default: "h-10 px-4 py-2", xl: "h-14 px-10 text-xl" };
  let spec = "";
  if (className?.includes("from-purple-500")) spec = "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600";
  else if (className?.includes("from-teal-500")) spec = "bg-gradient-to-r from-teal-500 to-green-500 text-white hover:from-teal-600 hover:to-green-600";
  else if (className?.includes("from-indigo-500")) spec = "bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600";
  else if (variant === "outline") spec = "border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300";

  return (
    <button
      ref={ref}
      className={cn(base, sizes[size] || sizes.default, spec || variants[variant] || variants.default, className)}
      {...props}
    >
      {children}
    </button>
  );
});
Button.displayName = "Button";

export default function PlayAndBuyLandingPage() {
  const { t } = useTranslation();
  const { lng } = useParams();
  const location = useLocation();
  const fallbackLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLng = (lng || fallbackLanguage || 'en').toLowerCase();
  const isFinnish = currentLng.startsWith('fi');

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const [isSubVideoPlaying, setIsSubVideoPlaying] = useState(false);

  useEffect(() => {
    if (currentLng && i18n.language !== currentLng) {
      i18n.changeLanguage(currentLng);
    }
  }, [currentLng]);

  const productRef = useRef(null);
  const aboutRef   = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);
  
  const subVideoRef = useRef(null);

  const handleRequestDemo = () => setShowContactModal(true);
  const handleScrollTo   = ref => ref.current?.scrollIntoView({ behavior: 'smooth' });

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeInOut" } }
  };
  const sectionTitleVariants = {
    hidden: { opacity: 0, y: -15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeInOut" } }
  };

  const goToPlay2Visit = () => {
    const targetLng = lng || 'en';
    window.location.href = `/${targetLng}/play2visit`;
  };
  const handleSelectSmallBusiness = () => {
    window.location.href = '/en';
  };
  const handleSelectBrandOwner = () => {
    goToPlay2Visit();
  };
  const handleLanguageChange = (code) => {
    if (!code) {
      return;
    }
    const normalized = code.toLowerCase();
    if (normalized === currentLng) {
      return;
    }
    const remainder = location.pathname.replace(/^\/[^/]+/, '') || '';
    const nextPath = `/${normalized}${remainder}`;
    const nextUrl = `${nextPath}${location.search}${location.hash}`;
    window.location.href = nextUrl;
  };

  const shouldShowAudienceOverlay = !lng;
  const productStepMedia = getProductStepMedia(isFinnish);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black text-white font-inter overflow-x-hidden">
      {/* ─── HEADER ─── */}
      <header className="fixed top-0 inset-x-0 z-50 h-16 bg-gray-900/70 backdrop-blur-lg">
        <div className="absolute inset-0 pointer-events-none">
          <Aurora colorStops={["#3A29FF","#FF94B4","#FF3232"]} blend={0.5} amplitude={1} speed={0.5} />
        </div>
        <nav className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-end">
          {/* Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <button onClick={() => handleScrollTo(productRef)} className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.product')}
            </button>
            <button onClick={() => handleScrollTo(pricingRef)} className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.pricing')}
            </button>
            <button onClick={() => handleScrollTo(aboutRef)} className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.about')}
            </button>
            <button onClick={handleRequestDemo} className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.contact')}
            </button>
            <button onClick={() => handleScrollTo(faqRef)} className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.faq')}
            </button>
            <a href="https://codespace.fi/fusionx/saas/public/user_login.php" className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.login')}
            </a>
            <button onClick={() => window.location.href="https://codespace.fi/fusionx/saas/create_checkout_session.php"} className="bg-teal-500 hover:bg-teal-600 text-white px-3 py-2 rounded-md text-sm font-medium">
              {t('nav.getStarted')}
            </button>
            <LanguageSelector value={currentLng} onChange={handleLanguageChange} />
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setMobileOpen(o => !o)} className="md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white">
            {mobileOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden absolute top-full inset-x-0 bg-gray-900/70 backdrop-blur-lg z-20">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['product','pricing','about','contact', 'faq'].map((key) => (
                <button
                  key={key}
                  onClick={() => { setMobileOpen(false); if(key==='contact') handleRequestDemo(); else handleScrollTo({ product:productRef, pricing:pricingRef, about:aboutRef, faq:faqRef }[key]) }}
                  className="block w-full text-left text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  {t(`nav.${key}`)}
                </button>
              ))}
              <a href="https://codespace.fi/fusionx/saas/public/user_login.php" onClick={() => setMobileOpen(false)} className="block w-full text-left text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
                {t('nav.login')}
              </a>
              <button onClick={() => { setMobileOpen(false); window.location.href="https://codespace.fi/fusionx/saas/create_checkout_session.php" }} className="block w-full text-left bg-teal-500 hover:bg-teal-600 text-white px-3 py-2 rounded-md text-sm font-medium">
                {t('nav.getStarted')}
              </button>
              <div className="mt-2 border-t border-gray-700 pt-2 px-2">
                <LanguageSelector value={currentLng} onChange={handleLanguageChange} className="w-full" />
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full h-[90svh] md:h-screen min-h-[560px] overflow-hidden">
      {/* background image */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <picture className="block h-full w-full">
          {/* desktop / large screens */}
          <source media="(min-width: 1024px)" srcSet="/hero-background-new.jpg" />
          {/* mobile default */}
          <img
            src="/hero-background-mobile.jpg"
            alt=""
            fetchPriority="high"
            className="h-full w-full object-cover object-[20%_center] md:object-center"
          />
        </picture>
      </div>

      {/* gradient overlay above image */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/70 via-black/40 to-transparent md:from-black/60 md:via-black/30" />

      {/* content on top */}
      <div className="relative z-20 container mx-auto px-4 h-full flex items-center justify-start">
        <div className="w-full md:w-1/2 text-left">
          <img
            src="play2visit_white.png"
            alt={t('hero.logoAlt')}
            className="h-24 md:h-40 w-auto mb-8"
          />
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className="text-white text-4xl md:text-6xl font-extrabold"
          >
            {t('hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut', delay: 0.3 }}
            className="text-lg md:text-xl text-gray-300 mt-4 md:mt-6 max-w-lg"
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeInOut', delay: 0.6 }}
            className="mt-8 flex justify-start"
          >
            <Button
              size="lg"
              onClick={() => (window.location.href = 'https://codespace.fi/fusionx/saas/create_checkout_session.php')}
              className="bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-base md:text-lg w-full sm:w-auto"
            >
              {t('hero.button')} <ClipboardCheck className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>


        {/* Sub-Headline */}
        <section className="container mx-auto px-4 py-16 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut', delay: 0.2 }}
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('subHeadline')}
          </motion.p>

          {isFinnish && (
            <motion.div
              key={currentLng}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeInOut', delay: 0.4 }}
              className="relative mt-8 aspect-video bg-gray-800 rounded-xl shadow-2xl border-2 border-purple-500/50 flex items-center justify-center"
            >

              <video
                ref={subVideoRef}
                className="rounded-lg w-full h-full object-cover"
                controls
                playsInline
                preload="none"
                poster="fi-subheadline-poster.webp"
                onPlay={() => setIsSubVideoPlaying(true)}
                onPause={() => setIsSubVideoPlaying(false)}
                onEnded={() => setIsSubVideoPlaying(false)}
              >
                <source src="Play2visit-versio3-720-fin.mp4" type="video/mp4" />
                {/* Optional: add a WebM source for better compression */}
                {/* <source src="/videos/Play2Visit_FI_Subheadline.webm" type="video/webm" /> */}
                Your browser does not support the video tag.
              </video>

              {/* Big play icon overlay — hidden while playing */}
              {!isSubVideoPlaying && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none transition-opacity duration-200">
                  <PlayCircle className="w-20 h-20 text-white/70" />
                </div>
              )}
            </motion.div>
          )}
        </section>


        {/* Product Section */}
        <section ref={productRef} id="product" className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={sectionTitleVariants}
              className="text-3xl md:text-4xl font-bold text-center mb-12 text-white"
            >
              {t('product.title')}
            </motion.h2>

            {/* Product banner image */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mx-auto mb-10 max-w-[820px] w-full"
            >
              <img
                src="/FX_banneri_820x357px-with-QR.png"
                alt={t('product.imageAlt')}
                className="w-full rounded-lg shadow-lg border border-gray-700/40 object-cover"
                style={{ aspectRatio: '820 / 357' }}
              />
            </motion.div>


            <div className="max-w-3xl mx-auto text-center">
              <motion.div
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-6">
                  {t('product.toolkit.title')}
                </h3>
                <p className="text-lg text-gray-300 mb-8">
                  <Trans
                    i18nKey="product.toolkit.text"
                    components={{
                      game:         <LinkInline href={LINKS.game} />,
                      qr:           <LinkInline href={LINKS.qr} />,
                      instructions: <LinkInline href={LINKS.instructions} />,
                      score:        <LinkInline href={LINKS.score} />,
                    }}
                  />
                </p>
                <ul className="space-y-4 text-left max-w-2xl mx-auto">
                  {['bullet1','bullet2','bullet3','bullet4'].map(key => {
                    const { title, text } = t(`product.${key}`, { returnObjects: true });
                    return (
                      <li key={key} className="flex items-start gap-3 text-gray-200">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                        <div>
                          <div className="text-white font-semibold">{title}</div>
                          <p className="text-sm text-gray-300">{text}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        <div className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {t('product.steps', { returnObjects: true }).map((step, index) => {
                const media = productStepMedia[index] || {};
                return (
                  <motion.div
                    key={step.title}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="flex flex-col rounded-2xl border border-white/10 bg-gray-900/60 p-6 shadow-lg"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-2xl">{step.label}</div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.35em] text-purple-300 font-semibold">
                          {t('product.stepLabel', { number: index + 1 })}
                        </p>
                        <h3 className="text-lg font-semibold text-white leading-tight">{step.title}</h3>
                      </div>
                    </div>
                    {(media.video || media.image) && (
                      <div
                        className="mb-4 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-black/40"
                        style={{ aspectRatio: '9 / 16' }}
                      >
                        {media.video ? (
                          <video
                            src={media.video}
                            poster={media.poster}
                            controls
                            playsInline
                            preload="metadata"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <img
                            src={media.image}
                            alt={step.title}
                            className="h-full w-full object-contain bg-black rounded-2xl"
                          />
                        )}
                      </div>
                    )}
                    <p className="text-sm text-gray-300 leading-relaxed">{step.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Gallery */}
        <section className="py-16 md:py-20 text-center">
          <div className="max-w-3xl mx-auto mb-12 px-4">
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-4">
              {t('gallery.title')}
            </h3>
            <p className="text-gray-300">{t('gallery.intro')}</p>
             <br />
            <p className="text-gray-300">{t('gallery.text')}</p>
            <br />

            <div className="mb-8">
              <img
                src="/demographics.jpg"
                alt={t('gallery.demographicsAlt')}
                className="mx-auto rounded-lg shadow-lg w-full max-w-md"
              />

              <p className="text-gray-300 italic mb-8">
                {t('gallery.demographics')}
              </p>
            </div>
          </div>
          {/* Trio of instruction images temporarily hidden per request */}
          {/* <div className="flex flex-col md:flex-row justify-center items-center space-y-8 md:space-y-0 md:space-x-8 px-4">
            <img src="/FusionX-ohje1.webp" alt="Step 1" className="w-full md:w-1/3 max-w-sm rounded-lg shadow-lg" />
            <img src="/FusionX-ohje2.webp" alt="Step 2" className="w-full md:w-1/3 max-w-sm rounded-lg shadow-lg" />
            <img src="/FusionX-ohje3.webp" alt="Step 3" className="w-full md:w-1/3 max-w-sm rounded-lg shadow-lg" />
          </div> */}

          <div className="text-center mt-8 space-y-2">
            <p className="text-sm text-gray-300">
              {t('gallery.mobilePrompt')}
            </p>
            <button
              onClick={() => window.open('https://codespace.fi/fusionx/51-demo.html', '_blank')}
              className="inline-block bg-purple-500 hover:bg-purple-600 text-white font-semibold px-6 py-3 rounded-full transition-colors duration-200"
            >
              {t('gallery.mobileButton')}
            </button>
          </div>
        </section>


        {/* Challenge */}
        <section className="bg-gray-800/50 py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6">
                <SplitText
                  key={t('challenge.title')}
                  text={t('challenge.title')}
                  className="inline-block"
                  delay={30}
                  duration={0.6}
                  ease="elastic.out(1, 0.3)"
                  splitType="chars"
                  from={{ opacity: 0, y: 40 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-100px"
                  textAlign="center"
                />
              </h2>
              <p className="text-gray-300 text-lg">{t('challenge.text')}</p>
              <br />
              <p className="text-xl font-semibold text-purple-400">{t('challenge.highlight')}</p>
            </div>
          </div>
        </section>

        {/* Solution */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6 flex flex-wrap justify-center items-center gap-2">
                <SplitText
                  key={t('solution.title')}
                  text={t('solution.title')}
                  className="inline-block"
                  delay={30}
                  duration={0.6}
                  ease="elastic.out(1, 0.3)"
                  splitType="chars"
                  from={{ opacity: 0, y: 40 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-100px"
                  textAlign="center"
                />
                <motion.span {...{ initial:{opacity:0,y:10}, animate:{opacity:1,y:0}, transition:{delay:1,duration:0.6,ease:'easeOut'} }}
                  className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500"
                >
                  {t('solution.play2visit')}
                </motion.span>
              </h2>
              <p className="text-gray-300 text-lg">{t('solution.text')}</p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-gray-800/50 py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <motion.h2 {...{ initial:"hidden", whileInView:"visible", viewport:{once:true,amount:0.3}, variants:sectionTitleVariants }}
              className="text-2xl md:text-3xl font-semibold text-white mb-12"
            >
              {t('howItWorks.title')}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
              {['step1','step2','step3','step4'].map((key, i) => {
                const step = t(`howItWorks.${key}`, { returnObjects: true });
                const Icon = [Gamepad2,Users,Store,TrendingUp][i];
                return (
                  <motion.div key={key} variants={cardVariants} className="text-center p-6 bg-gray-700/30 rounded-xl shadow-xl hover:shadow-purple-500/30 transition-shadow duration-300">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mx-auto flex items-center justify-center shadow-lg mb-4">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mt-4">{step.title}</h3>
                    <p className="text-gray-300 mt-2 text-sm">{step.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits Section */}
<section className="py-16 md:py-20">
  <div className="container mx-auto px-4">
    <h2 className="text-2xl md:text-3xl font-semibold text-white mb-12 text-center">
      {/* wrap “Play2Visit” in gradient as in the original */}
      {t('benefits.title').split('Play2Visit').map((part, i, arr) => (
        <React.Fragment key={i}>
          {part}
          {i < arr.length - 1 && (
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
              Play2Visit
            </span>
          )}
        </React.Fragment>
      ))}
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
      {[
        { key: 'traffic',    Icon: TrendingUp,  color: 'text-purple-400', x: -20, delay: 0   },
        { key: 'loyalty',    Icon: Trophy,      color: 'text-pink-400',   x:  20, delay: 0   },
        { key: 'experience', Icon: Smartphone,  color: 'text-blue-400',   x: -20, delay: 0.2 },
        { key: 'scale',      Icon: Settings,    color: 'text-green-400',  x:  20, delay: 0.2 },
        { key: 'custom',     Icon: Zap,         color: 'text-yellow-400', x: -20, delay: 0.4 },
        { key: 'insights',   Icon: TrendingUp,  color: 'text-red-400',    x:  20, delay: 0.4 }
      ].map(({ key, Icon, color, x, delay }) => {
        const { title, text } = t(`benefits.${key}`, { returnObjects: true });
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, x }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut', delay }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Icon className={`w-6 h-6 ${color}`} />
              {title}
            </h3>
            <p className="text-gray-300">
              <Trans
                i18nKey={`benefits.${key}.text`}
                components={{
                  sciencedirect: (
                    <a
                      href={LINKS.sciencedirect}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 underline hover:text-purple-300"
                    />
                  )
                }}
              />
            </p>
          </motion.div>
        );
      })}
      {/* The Power of Phygital */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.6 }}
        viewport={{ once: true }}
        className="space-y-4 col-span-full md:col-span-2"
      >
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Zap className="w-6 h-6 text-purple-400" />
          {t('benefits.phygital.title')}
        </h3>
        <p className="text-gray-300">{t('benefits.phygital.text')}</p>
      </motion.div>
    </div>
  </div>
</section>


        {/* Unlock Powerful Benefits (“about”) */}
        <section className="bg-gray-800/50 py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <motion.h2 {...{ initial:"hidden", whileInView:"visible", viewport:{once:true,amount:0.3}, variants:sectionTitleVariants }}
              className="text-3xl sm:text-4xl font-bold text-center mb-4"
            >
              {t('about.title')}
            </motion.h2>
            <motion.p {...{ initial:"hidden", whileInView:"visible", viewport:{once:true,amount:0.3}, variants:sectionTitleVariants }}
              className="text-lg text-center text-gray-400 mb-12"
            >
              {t('about.text')}
            </motion.p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {t('about.cards', { returnObjects: true }).map((card, i) => (
                <motion.div key={i} variants={cardVariants} className="bg-gray-800/60 p-8 rounded-lg shadow-xl border border-gray-700/50">
                  <h3 className={`text-2xl font-bold mb-6 ${i===0?'text-purple-400':i===1?'text-indigo-400':'text-green-400'}`}>
                    {card.title}
                  </h3>
                  <ul className="list-disc list-inside space-y-4 text-gray-300">
                    {card.items.map((_, j) => (
                      <li key={j}>
                        <Trans
                          i18nKey={`about.cards.${i}.items.${j}`}
                          components={{
                            mastercard: <LinkInline href={LINKS.mastercard} />,
                            sciencedirect: <LinkInline href={LINKS.sciencedirect} />,
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section
          /* tip: consider id="team" to avoid duplicate #about elsewhere */
          ref={aboutRef}
          id="team"
          className="bg-gray-800/50 py-16 px-4 text-center"
        >
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={sectionTitleVariants}
            className="text-3xl sm:text-4xl font-bold text-white mb-12"
          >
            {t('team.title')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 justify-items-center gap-12 md:gap-16 px-4 max-w-4xl mx-auto">
            {['kimmo', 'arto'].map((key) => {
              const p = t(`team.${key}`, { returnObjects: true });

              // Map filenames (adjust if your filenames differ)
              const avatarSrcByKey = {
                kimmo: 'kimmo.png',
                heidi: 'heidi.png',
                arto:  'arto.png', // <- the one we generated
              };
              const imgSrc = avatarSrcByKey[key] || `${key}.png`;

              return (
                <motion.div
                  key={key}
                  variants={cardVariants}
                  className="bg-gray-800/60 p-8 rounded-lg shadow-xl border border-gray-700/50 text-center flex flex-col items-center w-full max-w-lg sm:max-w-2xl"
                >
                  <img
                    src={imgSrc}
                    alt={p.name}
                    className={`w-36 h-36 rounded-full mb-6 border-4 ${borderColors[key] || 'border-gray-500'} object-cover`}
                  />

                  <h3 className="text-2xl font-bold text-white">{p.name}</h3>

                  <p className={`font-semibold ${roleColors[key] || 'text-gray-300'} mb-4`}>
                    {p.role}
                  </p>

                  <p className="text-gray-300 text-left mb-6">
                    <Trans
                      i18nKey={`team.${key}.text`}
                      components={TEAM_LINK_COMPONENTS[key] || {}}
                    />
                  </p>

                  <div className={`bg-purple-500/10 border-l-4 ${borderColors[key] || 'border-purple-400'} p-4 rounded-r-lg text-left w-full`}>
                    <h4 className="font-bold text-purple-300">{p.expertiseTitle}</h4>
                    <p className="text-gray-300 mt-1">{p.expertiseText}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>


        {/* Business Size */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <motion.h2 {...{ initial:"hidden", whileInView:"visible", viewport:{once:true,amount:0.3}, variants:sectionTitleVariants }}
              className="text-2xl md:text-3xl font-semibold text-white mb-12 text-center"
            >
              {t('businessSize.title')}
            </motion.h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {['enterprise','small'].map((sizeKey, idx) => {
                const bs = t(`businessSize.${sizeKey}`, { returnObjects: true });
                const Icon = sizeKey==='enterprise' ? Building : Briefcase;
                const grad = sizeKey==='enterprise'
                  ? "bg-gradient-to-r from-purple-500 to-pink-500"
                  : "bg-gradient-to-r from-teal-500 to-green-500";
                return (
                  <motion.div key={sizeKey} variants={cardVariants} className="bg-gray-800/60 p-8 rounded-xl shadow-2xl border border-gray-700/50 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center mb-6">
                        <Icon className={`w-12 h-12 mr-4 ${sizeKey==='enterprise'?'text-purple-400':'text-teal-400'}`}/>
                        <h3 className="text-2xl md:text-3xl font-bold text-white">{bs.title}</h3>
                      </div>
                      <p className="text-gray-300 mb-4 text-lg">{bs.text}</p>
                      <p className="text-gray-400 text-sm mb-6">{bs.subtext}</p>
                    </div>
                    <Button size="lg" onClick={sizeKey==='enterprise'?goToPlay2Visit:() => handleScrollTo(pricingRef)}
                      className={`w-full ${grad} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-lg`}
                    >
                      {bs.button} <ClipboardCheck className="w-6 h-6 ml-2" />
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
          <section
            ref={pricingRef}
            id="pricing"
            className="py-16 md:py-20"
          >
            <div className="container mx-auto px-4 text-center">
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionTitleVariants}
                className="text-3xl md:text-4xl font-bold text-white mb-4"
              >
                {t('pricing.title')}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                viewport={{ once: true, amount: 0.3 }}
                className="text-lg text-gray-300 max-w-3xl mx-auto mb-12 md:mb-16"
              >
                {t('pricing.subtitle')}
              </motion.p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch lg:gap-0">
                {/* Starter */}
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className="bg-gray-800/80 p-8 rounded-2xl lg:rounded-r-none shadow-lg border border-gray-700 flex flex-col justify-between z-0"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-cyan-400 mb-2">
                      {t('pricing.starter.name')}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {t('pricing.starter.icon')}
                    </p>
                    <p className="text-5xl font-bold text-white mb-2">
                      {t('pricing.starter.price')}
                      <span className="text-lg font-medium text-gray-400">
                        &nbsp;/ {t('pricing.starter.period')}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                      {t('pricing.starter.billing')}
                    </p>
                    <ul className="space-y-3 text-left mb-8">
                      {['bullet1','bullet2','bullet3'].map((b, i) => (
                        <li key={i} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                          {t(`pricing.starter.${b}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => window.location.href = "https://codespace.fi/fusionx/saas/create_checkout_session.php"}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500"
                  >
                    {t('pricing.starter.button')}
                  </Button>
                </motion.div>

                {/* Pro */}
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className="relative bg-gray-800 p-8 rounded-2xl shadow-2xl border-2 border-purple-500 flex flex-col justify-between transform lg:scale-110 z-10"
                >
                  <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">
                    {t('pricing.pro.popularBadge')}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-purple-400 mb-2">
                      {t('pricing.pro.name')}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {t('pricing.pro.icon')}
                    </p>
                    <p className="text-5xl font-bold text-white mb-2">
                      {t('pricing.pro.price')}
                      <span className="text-lg font-medium text-gray-400">
                        &nbsp;/ {t('pricing.pro.period')}
                      </span>
                    </p>
                    <div className="relative flex items-center justify-center group text-sm text-gray-500 mb-6">
                      <span>{t('pricing.pro.billing')}</span>
                      <Info className="w-4 h-4 ml-1.5 text-gray-400 cursor-pointer" />
                      <span className="absolute bottom-full mb-2 hidden group-hover:block w-60 bg-gray-700 text-white text-xs rounded-lg py-2 px-3 z-20 text-center shadow-lg">
                        {t('pricing.pro.tooltip')}
                      </span>
                    </div>
                    <ul className="space-y-3 text-left mb-8">
                      {['bullet1','bullet2','bullet3'].map((b, i) => (
                        <li key={i} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                          {t(`pricing.pro.${b}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    size="lg"
                    onClick={() => window.location.href = "https://codespace.fi/fusionx/saas/create_checkout_session2.php"}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                  >
                    {t('pricing.pro.button')}
                  </Button>
                </motion.div>

                {/* Elite */}
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className="bg-gray-800/80 p-8 rounded-2xl lg:rounded-l-none shadow-lg border border-gray-700 flex flex-col justify-between z-0"
                >
                  <div>
                    <h3 className="text-2xl font-bold text-green-400 mb-2">
                      {t('pricing.elite.name')}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      {t('pricing.elite.icon')}
                    </p>
                    <p className="text-5xl font-bold text-white mb-2">
                      {t('pricing.elite.price')}
                      <span className="text-lg font-medium text-gray-400">
                        &nbsp;/ {t('pricing.elite.period')}
                      </span>
                    </p>
                    <div className="relative flex items-center justify-center group text-sm text-gray-500 mb-6">
                      <span>{t('pricing.elite.billing')}</span>
                      <Info className="w-4 h-4 ml-1.5 text-gray-400 cursor-pointer" />
                      <span className="absolute bottom-full mb-2 hidden group-hover:block w-60 bg-gray-700 text-white text-xs rounded-lg py-2 px-3 z-20 text-center shadow-lg">
                        {t('pricing.elite.tooltip')}
                      </span>
                    </div>
                    <ul className="space-y-3 text-left mb-8">
                      {['bullet1','bullet2','bullet3'].map((b, i) => (
                        <li key={i} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                          {t(`pricing.elite.${b}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    size="lg"
                    onClick={() => window.location.href = "https://codespace.fi/fusionx/saas/create_checkout_session3.php"}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500"
                  >
                    {t('pricing.elite.button')}
                  </Button>
                </motion.div>
              </div>
            
            {/* ✅ Values section */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="mt-12"
              >
                <div className="mx-auto max-w-4xl rounded-2xl border border-emerald-500/40 bg-gray-800/60 p-6 md:p-8 shadow-xl text-left">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 rounded-full bg-emerald-500/10 p-3">
                      <Leaf className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg mb-1">
                        {t('values.title')}
                      </h3>
                      <p className="text-gray-300">
                        {t('values.co2')}
                      </p>
                      <p className="text-gray-300">
                        <Trans
                          i18nKey="values.co2Long"
                          components={{
                            stripe:   <LinkInline href={LINKS.stripeClimate} />
                          }}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* end values callout */}

            </div>
          </section>
          
          {/* FAQ Section */}
          <FAQSection ref={faqRef} onContact={handleRequestDemo} />

      {/* Footer */}
      <footer className="py-16 md:py-20 border-t border-gray-700/50 text-center">
        <p className="text-gray-500 text-sm">
          <Trans
            i18nKey="footer.line"
                values={{ year: new Date().getFullYear() }}
                components={{
                  codespace: <LinkInline href={LINKS.codespace} />,
                  privacy: <a href={LINKS.privacy} className="hover:text-white" />,
                  terms: <a href={LINKS.terms} className="hover:text-white" />,
                  sep: <span className="px-2" />
                }}
              />
        </p>
      </footer>
  </main>

      {shouldShowAudienceOverlay && (
        <div className="fixed inset-0 z-[120] bg-gray-950/95 backdrop-blur-2xl overflow-y-auto overflow-x-hidden">
          <div className="min-h-full px-4 py-8 flex items-start md:items-center justify-center">
            <div className="w-full max-w-5xl rounded-3xl border border-white/10 bg-gradient-to-br from-gray-900/90 via-gray-950/90 to-black/90 shadow-[0_45px_120px_rgba(0,0,0,0.65)] p-8 md:p-12 space-y-10">
              <div className="flex flex-col items-center text-center space-y-4">
                <img src="/Play2Visit-logo.png" alt="Play2Visit logo" className="h-24 w-auto" />
                <div>
                  <p id="audience-selector-title" className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                    Choose the Play2Visit site suitable for you
                  </p>
                  <p className="mt-3 text-base md:text-lg text-gray-300 max-w-2xl">
                    Pick the path that matches your goals.
                  </p>
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2" role="group" aria-label="Audience options">
                <button
                  type="button"
                  onClick={handleSelectSmallBusiness}
                  className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-3xl"
                >
                  <div className="h-full rounded-3xl border border-white/10 bg-gray-900/70 p-6 md:p-8 shadow-2xl transition duration-300 group-hover:-translate-y-1 group-hover:border-purple-400/60">
                    <div className="flex items-center gap-4 text-purple-200">
                      <div className="rounded-2xl bg-purple-500/15 p-3">
                        <Store className="w-8 h-8" />
                      </div>
                      <p className="text-xs uppercase tracking-[0.4em] text-purple-200/80">Small business</p>
                    </div>
                    <div className="mt-6 space-y-3">
                      <p className="text-2xl font-semibold text-white leading-tight">Plug-and-play engagement for local retailers</p>
                      <p className="text-gray-300 text-base">
                        Browse pricing, onboarding, and ready-made campaigns that turn footfall goals into simple playable journeys.
                      </p>
                    </div>
                    <div className="mt-8 flex items-center text-purple-200 font-semibold text-base">
                      Continue to the small business hub
                      <ChevronRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={handleSelectBrandOwner}
                  className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400 rounded-3xl"
                >
                  <div className="h-full rounded-3xl border border-pink-500/60 bg-gradient-to-br from-purple-900/60 via-gray-900/60 to-black/70 p-6 md:p-8 shadow-[0_35px_80px_rgba(236,72,153,0.15)] transition duration-300 group-hover:-translate-y-1">
                    <div className="flex items-center gap-4 text-pink-200">
                      <div className="rounded-2xl bg-pink-500/15 p-3">
                        <Building className="w-8 h-8" />
                      </div>
                      <p className="text-xs uppercase tracking-[0.4em] text-pink-200/80">Brands & enterprise</p>
                    </div>
                    <div className="mt-6 space-y-3">
                      <p className="text-2xl font-semibold text-white leading-tight">See the flagship Play2Visit brand experience</p>
                      <p className="text-gray-200 text-base">
                        For CMOs and brand teams looking for long-lasting engagement and real results from their marketing campaigns.
                      </p>
                    </div>
                    <div className="mt-8 flex items-center text-pink-200 font-semibold text-base">
                      Visit the Play2Visit brand showcase
                      <ChevronRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showContactModal && <ContactFormModal onClose={() => setShowContactModal(false)} />}
    </div>
  );
}
